import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where,
  collectionGroup,
  updateDoc,
  setDoc,
  Timestamp
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardActionArea,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  LinearProgress,
  CardContent,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Divider,
  Switch,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import {
  Search as SearchIcon,
  GridView as GridViewIcon,
  TableRows as TableRowsIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  BarChart as ChartIcon,
  PhoneAndroid as AppIcon,
  School as CourseIcon,
  CardMembership as FormulaIcon,
  ChildCare as ChildIcon,
  Language as WebIcon,
  FamilyRestroom as FamilyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StudentCard from '../components/students/StudentCard';
import { CSVLink } from 'react-csv';
import moment from 'moment';

function Students() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterCourse, setFilterCourse] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [courses, setCourses] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // ── Édition inline dans le dialog ──────────────────────────────
  const [editMode,      setEditMode]      = useState(false);
  const [editData,      setEditData]      = useState({});
  const [savingEdit,    setSavingEdit]    = useState(false);
  const [editError,     setEditError]     = useState('');
  const [allCoursesWithIds, setAllCoursesWithIds] = useState([]); // {id, title}
  const navigate = useNavigate();

  // Fonction pour calculer le taux de présence pour un étudiant
  const calculateAttendanceRate = async (studentId) => {
    try {
      // Récupérer toutes les présences de l'étudiant
      const attendanceQuery = query(
        collectionGroup(db, 'records'),
        where('studentId', '==', studentId)
      );
      
      const attendanceSnap = await getDocs(attendanceQuery);
      
      let totalSessions = 0;
      let attendedSessions = 0;
      
      attendanceSnap.forEach(doc => {
        const data = doc.data();
        totalSessions++;
        if (data.present) {
          attendedSessions++;
        }
      });
      
      return totalSessions > 0 ? Math.round((attendedSessions / totalSessions) * 100) : undefined;
    } catch (error) {
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        // Index Firestore manquant — cliquer le lien dans la console pour le créer automatiquement :
        // Collection group: records | Champs: studentId ASC
        console.warn('⚠️ Index Firestore requis pour les présences. Créez-le via le lien affiché ci-dessus.');
      } else {
        console.error("Erreur lors du calcul du taux de présence:", error);
      }
      return undefined; // undefined = barre de présence masquée (pas de données)
    }
  };

  // Charger les statistiques de présence pour tous les étudiants
  const loadAttendanceStats = async (studentsList) => {
    setLoadingAttendance(true);
    try {
      const studentsWithAttendance = await Promise.all(
        studentsList.map(async (student) => {
          const attendanceRate = await calculateAttendanceRate(student.id);
          return {
            ...student,
            attendanceRate: Math.round(attendanceRate)
          };
        })
      );
      
      setStudents(studentsWithAttendance);
      setFilteredStudents(studentsWithAttendance);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques de présence:", error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  // Actualiser les statistiques de présence
  const refreshAttendanceStats = async () => {
    await loadAttendanceStats(students);
  };

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        // Récupérer tous les étudiants
        const studentsSnapshot = await getDocs(collection(db, 'students'));
        const studentsList = [];
        const coursesSet = new Set();
        
        // Récupérer toutes les inscriptions pour avoir les cours et formules
        const registrationsSnapshot = await getDocs(collection(db, 'registrations'));
        const registrationsMap = new Map();
        
        // Organiser les inscriptions par studentId
        registrationsSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.persons) {
            data.persons.forEach(person => {
              if (person.studentId) {
                if (!registrationsMap.has(person.studentId)) {
                  registrationsMap.set(person.studentId, []);
                }
                registrationsMap.get(person.studentId).push({
                  id: doc.id,
                  ...data,
                  personInfo: person
                });
              }
            });
          }
        });

        // ── Aussi charger depuis users/ (rôle Élève/Élève adulte) ──────────
        // Un élève qui vient de s'inscrire via l'app est dans users/ mais pas encore
        // dans students/ (sync Users.js pas encore lancé depuis leur inscription).
        const appUsersSnap = await getDocs(collection(db, 'users'));
        const existingStudentIds = new Set(studentsSnapshot.docs.map(d => d.id));
        const extraStudentDocs = [];
        appUsersSnap.docs.forEach(d => {
          const role = d.data().role || '';
          const isStudentRole = ['Élève', 'Élève adulte', 'eleve', 'eleve_adult'].includes(role);
          if (isStudentRole && !existingStudentIds.has(d.id)) {
            extraStudentDocs.push(d); // élève dans users/ mais pas dans students/
          }
        });

        // Combiner les deux sources
        const allStudentDocs = [...studentsSnapshot.docs, ...extraStudentDocs];

        for (const studentDoc of allStudentDocs) {
          const studentData = {
            id: studentDoc.id,
            ...studentDoc.data(),
            registrations: [],
            attendanceRate: 0
          };

          // Récupérer les inscriptions de cet étudiant
          const studentRegistrations = registrationsMap.get(studentDoc.id) || [];
          studentData.registrations = studentRegistrations;

          // Récupérer les informations du parent si parentId existe
          if (studentData.parentId) {
            try {
              const parentDoc = await getDoc(doc(db, 'users', studentData.parentId));
              if (parentDoc.exists()) {
                studentData.parentInfo = {
                  firstName: parentDoc.data().firstName || '',
                  lastName: parentDoc.data().lastName || '',
                  email: parentDoc.data().email || '',
                  phone: parentDoc.data().phone || ''
                };
              }
            } catch (error) {
              console.error('Erreur lors de la récupération du parent:', error);
            }
          }

          // Récupérer les informations du cours si courseId existe
          if (studentData.courseId) {
            try {
              const courseDoc = await getDoc(doc(db, 'dance_courses', studentData.courseId));
              if (courseDoc.exists()) {
                studentData.courseInfo = {
                  id: courseDoc.id,
                  title: courseDoc.data().title || '',
                  title_i18n: courseDoc.data().title_i18n || {}
                };
              }
            } catch (error) {
              console.error('Erreur lors de la récupération du cours:', error);
            }
          }

          // Ajouter les cours des inscriptions à la liste des cours disponibles
          studentRegistrations.forEach(registration => {
            if (registration.courseTitle) {
              coursesSet.add(registration.courseTitle);
            }
          });

          studentsList.push(studentData);
        }

        // Récupérer tous les cours disponibles depuis la collection dance_courses
        try {
          const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
          const coursesList = [];
          coursesSnapshot.forEach((doc) => {
            const courseData = doc.data();
            const courseTitle = courseData.title_i18n?.fr || courseData.title;
            if (courseTitle) {
              coursesSet.add(courseTitle);
              coursesList.push({ id: doc.id, title: courseTitle });
            }
          });
          coursesList.sort((a, b) => a.title.localeCompare(b.title));
          setAllCoursesWithIds(coursesList);
        } catch (error) {
          console.error('Erreur lors de la récupération des cours:', error);
        }

        // Déduplication par ID (et non par nom) pour ne pas perdre les élèves
        // adultes qui auraient le même prénom/nom qu'un mineur.
        // Si le même ID apparaît deux fois (sync Users.js), on fusionne les inscriptions.
        const uniqueStudentsMap = new Map();
        studentsList.forEach(student => {
          const key = student.id; // ← clé = ID Firebase, pas le nom
          if (!uniqueStudentsMap.has(key)) {
            uniqueStudentsMap.set(key, student);
          } else {
            // Même ID = même personne → fusionner uniquement les inscriptions
            const existing = uniqueStudentsMap.get(key);
            const existingIds = new Set(existing.registrations.map(r => r.id));
            student.registrations.forEach(r => {
              if (!existingIds.has(r.id)) existing.registrations.push(r);
            });
          }
        });

        const uniqueStudents = Array.from(uniqueStudentsMap.values());

        // ── Statut compte selon la source d'inscription ─────────────────
        // accountStatus:
        //   'app'      → inscrit via l'app mobile (registrationSource='mobile_app'
        //                 ou emailVerificationRequired=true dans users/)
        //   'firebase' → a un compte Firebase Auth (site web ou admin avec email)
        //   'none'     → créé par l'admin sans Firebase Auth (id manual_*)
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          const usersMap = new Map(usersSnap.docs.map(d => [d.id, d.data()]));
          uniqueStudents.forEach(s => {
            if (s.id.startsWith('manual_')) {
              s.accountStatus = 'none';
            } else {
              const ud = usersMap.get(s.id);
              if (!ud) {
                s.accountStatus = 'none';
              } else {
                // pseudo = champ obligatoire dans registration_form.dart → discriminant fiable
                // Les inscriptions site web / admin n'ont généralement pas de pseudo.
                const hasPseudo = ud.pseudo && ud.pseudo.trim() !== '';
                if (
                  hasPseudo ||
                  ud.registrationSource === 'mobile_app' ||
                  ud.emailVerificationRequired === true
                ) {
                  s.accountStatus = 'app';      // inscrit via l'app mobile
                } else {
                  s.accountStatus = 'firebase'; // Firebase Auth mais pas via l'app
                }
              }
            }
            s.hasAppAccount = s.accountStatus === 'app';
          });
        } catch (err) {
          console.warn('accountStatus check:', err.message);
        }

        // ── Marquer les élèves liés à un compte parent dans l'app ──
        // On charge parents/ une seule fois et on construit un index (uid → parent)
        // et (prénom-nom → parent) pour les enfants sans UID.
        // Le résultat est stocké dans student.parentAppLink pour l'affichage.
        try {
          const parentsSnap = await getDocs(collection(db, 'parents'));
          const byUid  = new Map();
          const byName = new Map();
          for (const pDoc of parentsSnap.docs) {
            const pd = pDoc.data();
            const parentLabel = [pd.parentFirstName ?? pd.firstName, pd.parentLastName ?? pd.lastName]
              .filter(Boolean).join(' ') || pd.email || pDoc.id;
            for (const child of (pd.children ?? [])) {
              if (child.uid)  byUid.set(child.uid,  { label: parentLabel, parentUid: pDoc.id });
              else {
                const key = `${child.firstName}-${child.lastName}`.toLowerCase();
                byName.set(key, { label: parentLabel, parentUid: pDoc.id });
              }
            }
          }
          uniqueStudents.forEach(s => {
            s.parentAppLink = byUid.get(s.id)
              ?? byName.get(`${s.firstName}-${s.lastName}`.toLowerCase())
              ?? null;
          });
        } catch (err) {
          console.warn('Cross-ref parents app:', err.message);
        }

        setCourses(Array.from(coursesSet).filter(Boolean).sort());
        
        // Charger les statistiques de présence
        await loadAttendanceStats(uniqueStudents);
        setLoading(false);
      } catch (error) {
        console.error('Error loading students:', error);
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  // Filtrage des élèves
  useEffect(() => {
    let filtered = students;

    // Filtre par recherche textuelle
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(student =>
        student.firstName?.toLowerCase().includes(searchLower) ||
        student.lastName?.toLowerCase().includes(searchLower) ||
        student.email?.toLowerCase().includes(searchLower) ||
        student.phone?.includes(searchTerm) ||
        (student.parentInfo && 
          `${student.parentInfo.firstName} ${student.parentInfo.lastName}`
            .toLowerCase().includes(searchLower)) ||
        // Recherche dans les cours des inscriptions
        student.registrations.some(reg => 
          reg.courseTitle?.toLowerCase().includes(searchLower)
        ) ||
        // Recherche dans les formules des inscriptions
        student.registrations.some(reg => 
          reg.planName?.toLowerCase().includes(searchLower)
        )
      );
    }

    // Filtre par cours
    if (filterCourse) {
      filtered = filtered.filter(student => 
        student.registrations.some(reg => 
          reg.courseTitle === filterCourse
        )
      );
    }

    // Filtre par statut
    if (filterStatus) {
      if (filterStatus === 'active') {
        filtered = filtered.filter(student => student.isActive !== false);
      } else if (filterStatus === 'inactive') {
        filtered = filtered.filter(student => student.isActive === false);
      }
    }

    setFilteredStudents(filtered);
  }, [searchTerm, students, filterCourse, filterStatus]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedStudent(null);
    setEditMode(false);
    setEditData({});
    setEditError('');
  };

  // Ouvre le mode édition dans le dialog avec les données pré-remplies
  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setDialogOpen(true);
    setEditMode(true);
    setEditError('');
    // Pré-remplir avec les données existantes de l'élève
    const dob = student.dateOfBirth
      ? (student.dateOfBirth.seconds
          ? new Date(student.dateOfBirth.seconds * 1000).toISOString().split('T')[0]
          : moment(student.dateOfBirth).format('YYYY-MM-DD'))
      : '';
    setEditData({
      firstName:         student.firstName        || '',
      lastName:          student.lastName         || '',
      email:             student.email            || '',
      phone:             student.phone            || '',
      dateOfBirth:       dob,
      role:              student.role             || 'Élève',
      selectedCourseIds: student.selectedCourseIds || (student.courseId ? [student.courseId] : []),
      registrationPlan:  student.registrationPlan  || student.registrations?.[0]?.planName || '',
      isActive:          student.isActive !== false,
      pseudo:            student.pseudo           || '',
    });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCoursesChange = (e) => {
    setEditData(prev => ({ ...prev, selectedCourseIds: e.target.value }));
  };

  const handleSaveEdit = async () => {
    if (!selectedStudent) return;
    setSavingEdit(true);
    setEditError('');
    try {
      const dateOfBirthTimestamp = editData.dateOfBirth
        ? Timestamp.fromDate(new Date(editData.dateOfBirth))
        : null;

      const updated = {
        firstName:         editData.firstName,
        lastName:          editData.lastName,
        email:             editData.email,
        phone:             editData.phone,
        dateOfBirth:       dateOfBirthTimestamp,
        role:              editData.role,
        pseudo:            editData.pseudo,
        selectedCourseIds: editData.selectedCourseIds,
        courseId:          editData.selectedCourseIds[0] || '',
        registrationPlan:  editData.registrationPlan,
        isActive:          editData.isActive,
        updatedAt:         Timestamp.now(),
      };

      // Mettre à jour users/{id} si c'est un vrai UID (pas manual_)
      if (!selectedStudent.id.startsWith('manual_')) {
        await setDoc(doc(db, 'users', selectedStudent.id), updated, { merge: true });
      }

      // Mettre à jour students/{id}
      await setDoc(doc(db, 'students', selectedStudent.id), {
        ...updated,
        userId: selectedStudent.id,
      }, { merge: true });

      // Mettre à jour le state local pour rafraîchir sans recharger
      const refreshed = { ...selectedStudent, ...updated,
        dateOfBirth: editData.dateOfBirth,
        courseInfo: allCoursesWithIds.find(c => c.id === editData.selectedCourseIds[0]) || selectedStudent.courseInfo,
      };
      setStudents(prev => prev.map(s => s.id === selectedStudent.id ? refreshed : s));
      setFilteredStudents(prev => prev.map(s => s.id === selectedStudent.id ? refreshed : s));
      setSelectedStudent(refreshed);
      setEditMode(false);
    } catch (err) {
      console.error('handleSaveEdit:', err);
      setEditError("Erreur lors de la sauvegarde : " + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteStudent = async (student) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${student.firstName} ${student.lastName} ?`)) {
      try {
        setStudents(students.filter(s => s.id !== student.id));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const getActiveStudentsCount = () => {
    return students.filter(student => student.isActive !== false).length;
  };

  const getInactiveStudentsCount = () => {
    return students.filter(student => student.isActive === false).length;
  };

  // Calculer la moyenne des présences
  const getAverageAttendance = () => {
    const studentsWithAttendance = students.filter(student => student.attendanceRate > 0);
    if (studentsWithAttendance.length === 0) return 0;
    
    const total = studentsWithAttendance.reduce((sum, student) => sum + student.attendanceRate, 0);
    return Math.round(total / studentsWithAttendance.length);
  };

  // Préparation des données pour l'export CSV
  const csvData = filteredStudents.map(student => {
    const allCourses = [...new Set(student.registrations.map(reg => reg.courseTitle).filter(Boolean))];
    const allPlans = [...new Set(student.registrations.map(reg => reg.planName).filter(Boolean))];
    
    return {
      'Prénom': student.firstName || '',
      'Nom': student.lastName || '',
      'Email': student.email || '',
      'Téléphone': student.phone || '',
      'Date de naissance': student.dateOfBirth 
        ? moment(student.dateOfBirth).format('DD/MM/YYYY') 
        : '',
      'Âge': student.dateOfBirth 
        ? moment().diff(moment(student.dateOfBirth), 'years') 
        : '',
      'Cours': allCourses.join('; ') || '',
      'Formules': allPlans.join('; ') || '',
      'Taux de présence': `${student.attendanceRate}%`,
      'Statut': student.isActive !== false ? 'Actif' : 'Inactif',
      'Parent': student.parentInfo ? 
        `${student.parentInfo.firstName} ${student.parentInfo.lastName}` : 
        '',
      'Email Parent': student.parentInfo?.email || '',
      'Téléphone Parent': student.parentInfo?.phone || '',
      'Nombre d\'inscriptions': student.registrations.length,
      'Dernière inscription': student.registrations.length > 0 
        ? moment(student.registrations[0].createdAt?.toDate()).format('DD/MM/YYYY HH:mm')
        : ''
    };
  });

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Chargement des élèves...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête avec statistiques */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <ChartIcon sx={{ mr: 1, color: '#7C3AED' }} />
            Gestion des Élèves
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${students.length}`} 
              color="primary" 
              variant="outlined" 
            />
            <Chip 
              label={`Actifs: ${getActiveStudentsCount()}`} 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              label={`Inactifs: ${getInactiveStudentsCount()}`} 
              color="default" 
              variant="outlined" 
            />
            <Chip 
              label={`Présence moyenne: ${getAverageAttendance()}%`} 
              color="info" 
              variant="outlined" 
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={refreshAttendanceStats}
            startIcon={<RefreshIcon />}
            disabled={loadingAttendance}
          >
            Actualiser les stats
          </Button>
          <CSVLink 
            data={csvData} 
            filename={`eleves-${moment().format('YYYYMMDD-HHmm')}.csv`}
            style={{ textDecoration: 'none' }}
          >
            <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />}
            >
              Exporter CSV
            </Button>
          </CSVLink>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new?role=Élève')}
          >
            Nouvel élève
          </Button>
        </Box>
      </Box>

      {/* Barre de recherche et filtres */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Rechercher un élève, parent, cours ou formule..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: '300px', flexGrow: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Filtrer par cours</InputLabel>
            <Select
              value={filterCourse}
              label="Filtrer par cours"
              onChange={(e) => setFilterCourse(e.target.value)}
            >
              <MenuItem value="">Tous les cours</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course} value={course}>
                  {course}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Statut</InputLabel>
            <Select
              value={filterStatus}
              label="Statut"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="active">Actifs</MenuItem>
              <MenuItem value="inactive">Inactifs</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="mode d'affichage"
          >
            <ToggleButton value="grid" aria-label="vue grille">
              <GridViewIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="vue liste" disabled>
              <TableRowsIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Résultats du filtrage */}
        {filterCourse || filterStatus || searchTerm ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              {filteredStudents.length} élève(s) trouvé(s)
              {filterCourse && ` dans le cours "${filterCourse}"`}
              {filterStatus && ` avec le statut "${filterStatus === 'active' ? 'Actif' : 'Inactif'}"`}
              {searchTerm && ` pour la recherche "${searchTerm}"`}
            </Typography>
            <Button 
              size="small" 
              onClick={() => {
                setFilterCourse('');
                setFilterStatus('');
                setSearchTerm('');
              }}
            >
              Effacer les filtres
            </Button>
          </Box>
        ) : null}
      </Box>

      {/* Liste des élèves */}
      {filteredStudents.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '300px',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="h6" color="textSecondary">
            {searchTerm || filterCourse || filterStatus ? 'Aucun élève trouvé' : 'Aucun élève inscrit'}
          </Typography>
          {!searchTerm && !filterCourse && !filterStatus && (
            <Button 
              variant="contained" 
              onClick={() => navigate('/users/new?role=Élève')}
            >
              Ajouter le premier élève
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredStudents.map((student) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
              <StudentCompactCard
                student={student}
                onClick={() => handleStudentClick(student)}
                onEdit={() => handleEditStudent(student)}
                onDelete={() => handleDeleteStudent(student)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog vue + édition inline ── */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedStudent?.firstName} {selectedStudent?.lastName}
            </Typography>
            {editMode
              ? <Chip label="Mode édition" color="warning" size="small" />
              : <>
                  {selectedStudent?.accountStatus === 'app'
                    ? <Chip icon={<AppIcon style={{ fontSize: 12 }} />} label="App" size="small" color="success" />
                    : selectedStudent?.accountStatus === 'firebase'
                      ? <Chip icon={<WebIcon style={{ fontSize: 12 }} />} label="Web/Admin" size="small" color="info" variant="outlined" />
                      : <Chip label="Sans compte" size="small" variant="outlined" />
                  }
                  {(selectedStudent?.role === 'Élève' || selectedStudent?.role === 'eleve') && (
                    <Chip icon={<ChildIcon style={{ fontSize: 12 }} />} label="Mineur" size="small" color="secondary" variant="outlined" />
                  )}
                  {selectedStudent?.parentAppLink && (
                    <Chip label={`Parent : ${selectedStudent.parentAppLink.label}`} size="small" color="primary" variant="outlined" />
                  )}
                </>
            }
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {selectedStudent && !editMode && (
            /* ── Vue consultation ── */
            <Box>
              {/* Cours + formule */}
              {(selectedStudent.courseInfo?.title || selectedStudent.registrations?.[0]?.courseTitle ||
                selectedStudent.selectedCourseIds?.length > 0) && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {selectedStudent.selectedCourseIds?.length > 0
                    ? selectedStudent.selectedCourseIds.map(cid => {
                        const c = allCoursesWithIds.find(x => x.id === cid);
                        return c ? <Chip key={cid} icon={<CourseIcon style={{ fontSize: 13 }} />}
                          label={c.title} size="small" variant="outlined" /> : null;
                      })
                    : <Chip icon={<CourseIcon style={{ fontSize: 13 }} />}
                        label={selectedStudent.courseInfo?.title_i18n?.fr || selectedStudent.courseInfo?.title
                          || selectedStudent.registrations?.[0]?.courseTitle}
                        size="small" variant="outlined" />
                  }
                  {(selectedStudent.registrationPlan || selectedStudent.registrations?.[0]?.planName) && (
                    <Chip icon={<FormulaIcon style={{ fontSize: 13 }} />}
                      label={selectedStudent.registrationPlan || selectedStudent.registrations?.[0]?.planName}
                      size="small" color="secondary" variant="outlined" />
                  )}
                </Box>
              )}

              {/* Infos personnelles */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                {[
                  { label: 'Email',     val: selectedStudent.email },
                  { label: 'Téléphone', val: selectedStudent.phone },
                  { label: 'Naissance', val: selectedStudent.dateOfBirth
                      ? moment(selectedStudent.dateOfBirth?.seconds
                          ? selectedStudent.dateOfBirth.seconds * 1000
                          : selectedStudent.dateOfBirth).format('DD/MM/YYYY')
                      : null },
                  { label: 'Âge',       val: selectedStudent.dateOfBirth
                      ? moment().diff(moment(selectedStudent.dateOfBirth?.seconds
                          ? selectedStudent.dateOfBirth.seconds * 1000
                          : selectedStudent.dateOfBirth), 'years') + ' ans'
                      : null },
                  { label: 'Pseudo',    val: selectedStudent.pseudo },
                  { label: 'Statut',    val: selectedStudent.isActive !== false ? '✅ Actif' : '⛔ Inactif' },
                ].filter(r => r.val).map(({ label, val }) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                      {label}
                    </Typography>
                    <Typography fontSize={13}>{val}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Présence */}
              {selectedStudent.attendanceRate !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    Taux de présence
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <LinearProgress variant="determinate" value={selectedStudent.attendanceRate}
                      sx={{ flex: 1, height: 8, borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: selectedStudent.attendanceRate >= 75 ? '#10B981'
                            : selectedStudent.attendanceRate >= 50 ? '#F59E0B' : '#EF4444'
                        }}} />
                    <Typography fontSize={13} fontWeight={600}>{selectedStudent.attendanceRate}%</Typography>
                  </Box>
                </Box>
              )}

              {/* Parent */}
              {selectedStudent.parentInfo && (
                <Box sx={{ p: 1.5, bgcolor: '#F5F3FF', borderRadius: 1.5, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                    Parent
                  </Typography>
                  <Typography fontSize={13} fontWeight={600}>
                    {selectedStudent.parentInfo.firstName} {selectedStudent.parentInfo.lastName}
                  </Typography>
                  {selectedStudent.parentInfo.email && (
                    <Typography fontSize={12} color="text.secondary">{selectedStudent.parentInfo.email}</Typography>
                  )}
                  {selectedStudent.parentInfo.phone && (
                    <Typography fontSize={12} color="text.secondary">{selectedStudent.parentInfo.phone}</Typography>
                  )}
                </Box>
              )}

              {/* Inscriptions */}
              {selectedStudent.registrations?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, display: 'block', mb: 1 }}>
                    Inscriptions ({selectedStudent.registrations.length})
                  </Typography>
                  {selectedStudent.registrations.slice(0, 3).map((reg, i) => (
                    <Box key={i} sx={{ p: 1.2, mb: 0.8, bgcolor: 'grey.50', borderRadius: 1, fontSize: 12 }}>
                      <strong>{reg.courseTitle || 'Cours'}</strong>
                      {reg.planName && ` · ${reg.planName}`}
                      {reg.createdAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {moment(reg.createdAt.toDate?.() || reg.createdAt).format('DD/MM/YYYY')}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {selectedStudent && editMode && (
            /* ── Formulaire d'édition ── */
            <Box sx={{ pt: 1 }}>
              {editError && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FEF2F2', borderRadius: 1, color: '#DC2626', fontSize: 13 }}>
                  {editError}
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField label="Prénom *" name="firstName" size="small"
                  value={editData.firstName} onChange={handleEditChange} required />
                <TextField label="Nom *" name="lastName" size="small"
                  value={editData.lastName} onChange={handleEditChange} required />
                <TextField label="Email" name="email" type="email" size="small"
                  value={editData.email} onChange={handleEditChange} />
                <TextField label="Téléphone" name="phone" size="small"
                  value={editData.phone} onChange={handleEditChange} />
                <TextField label="Date de naissance" name="dateOfBirth" type="date" size="small"
                  value={editData.dateOfBirth} onChange={handleEditChange}
                  InputLabelProps={{ shrink: true }} />
                <TextField label="Pseudo" name="pseudo" size="small"
                  value={editData.pseudo} onChange={handleEditChange}
                  helperText="Identifie les comptes App" />
                <FormControl size="small">
                  <InputLabel>Rôle</InputLabel>
                  <Select name="role" value={editData.role} onChange={handleEditChange} label="Rôle">
                    <MenuItem value="Élève">Élève (mineur)</MenuItem>
                    <MenuItem value="Élève adulte">Élève adulte</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Formule</InputLabel>
                  <Select name="registrationPlan" value={editData.registrationPlan}
                    onChange={handleEditChange} label="Formule">
                    <MenuItem value="">Aucune</MenuItem>
                    {['essai','unique','carte10','bronze','argent','annuel','formation_crew'].map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Multi-select cours */}
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cours inscrits</InputLabel>
                  <Select multiple value={editData.selectedCourseIds || []}
                    onChange={handleCoursesChange}
                    input={<OutlinedInput label="Cours inscrits" />}
                    renderValue={sel => sel.length === 0 ? 'Aucun'
                      : sel.map(id => allCoursesWithIds.find(c => c.id === id)?.title || id).join(', ')}>
                    {allCoursesWithIds.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        <Checkbox checked={(editData.selectedCourseIds || []).includes(c.id)} />
                        <ListItemText primary={c.title} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(editData.selectedCourseIds || []).length} cours sélectionné(s)
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={<Switch name="isActive" checked={editData.isActive !== false}
                    onChange={e => setEditData(p => ({ ...p, isActive: e.target.checked }))} />}
                  label="Compte actif"
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={() => { setEditMode(false); setEditError(''); }}>Annuler</Button>
              <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}
                sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>
                {savingEdit ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Fermer</Button>
              <Button variant="outlined"
                onClick={() => selectedStudent && handleEditStudent(selectedStudent)}
                sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}>
                ✏️ Modifier
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Carte élève compacte
// ═══════════════════════════════════════════════════════════════════
function StudentCompactCard({ student, onClick, onEdit, onDelete }) {
  const initials = `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase() || '?';
  const isChild  = student.role === 'Élève' || student.role === 'eleve';
  const isAdult  = student.role === 'Élève adulte' || student.role === 'eleve_adult';

  // Cours : priorité courseInfo, sinon première inscription
  const courseTitle = student.courseInfo?.title_i18n?.fr
    || student.courseInfo?.title
    || student.registrations?.[0]?.courseTitle
    || '';
  // Formule : première inscription
  const formula = student.registrations?.[0]?.planName || student.registrationPlan || '';

  // Badge compte
  const accountBadge = () => {
    if (student.accountStatus === 'app')
      return <Chip icon={<AppIcon style={{ fontSize: 11 }} />} label="App" size="small" color="success"
        sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }} />;
    if (student.accountStatus === 'firebase')
      return <Chip icon={<WebIcon style={{ fontSize: 11 }} />} label="Web" size="small" color="info" variant="outlined"
        sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }} />;
    return <Chip label="Sans compte" size="small" variant="outlined" color="default"
      sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }} />;
  };

  return (
    <Card variant="outlined" onClick={onClick} sx={{
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      '&:hover': { boxShadow: '0 4px 14px rgba(124,58,237,0.12)', borderColor: '#7C3AED' },
      borderColor: student.accountStatus === 'app' ? 'rgba(124,58,237,0.25)' : 'divider',
    }}>
      <Box sx={{ p: 1.8 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          {/* Avatar */}
          <Avatar sx={{
            bgcolor: student.accountStatus === 'app' ? '#7C3AED' : '#9CA3AF',
            width: 42, height: 42, fontSize: 15, fontWeight: 700, flexShrink: 0
          }}>
            {initials}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Nom + statut */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3, flexWrap: 'wrap' }}>
              <Typography fontWeight={700} fontSize={13} noWrap>
                {student.firstName} {student.lastName}
              </Typography>
              {isChild && (
                <Chip icon={<ChildIcon style={{ fontSize: 10 }} />} label="Mineur"
                  size="small" variant="outlined" color="secondary"
                  sx={{ height: 18, fontSize: 9, '& .MuiChip-label': { px: 0.5 } }} />
              )}
            </Box>

            {/* Cours */}
            {courseTitle && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                <CourseIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography fontSize={11} color="text.secondary" noWrap>{courseTitle}</Typography>
              </Box>
            )}

            {/* Formule */}
            {formula && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.4 }}>
                <FormulaIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                <Typography fontSize={11} color="text.secondary" noWrap>{formula}</Typography>
              </Box>
            )}

            {/* Présence */}
            {student.attendanceRate !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
                <Box sx={{
                  width: 60, height: 4, borderRadius: 2,
                  bgcolor: '#E5E7EB', overflow: 'hidden'
                }}>
                  <Box sx={{
                    width: `${student.attendanceRate}%`, height: '100%',
                    bgcolor: student.attendanceRate >= 75 ? '#10B981'
                           : student.attendanceRate >= 50 ? '#F59E0B' : '#EF4444',
                    borderRadius: 2,
                  }} />
                </Box>
                <Typography fontSize={10} color="text.secondary">{student.attendanceRate}%</Typography>
              </Box>
            )}

            {/* Badges */}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {accountBadge()}
              {student.parentAppLink && (
                <Chip label={`Parent lié`} size="small" color="primary" variant="outlined"
                  sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.6 } }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

export default Students;